// api/tasks.ts
import axios from "axios";
import type { Task } from "../../types/tasks";
import { backend_url } from "../../constants";
import { AXIOS_CONFIG } from "../api";
const API_URL = `${backend_url}/api/management/tasks/`;

/**
 * Get a list of tasks, optionally filtered by completion status.
 *
 * @param filters Optional filters such as `completed: boolean`.
 * @returns A list of tasks.
 */
export const getTasks = async (filters?: {
  completed?: boolean;
}): Promise<Task[]> => {
  try {
    const params: any = {};

    if (filters?.completed !== undefined) {
      params.completed = filters.completed;
    }

    const response = await axios.get(API_URL, {
      params,
      ...AXIOS_CONFIG,
    });

    return response.data.content;
  } catch (error) {
    console.error("Error fetching tasks:", error);
    throw error;
  }
};

/**
 * Create a new task.
 *
 * @param taskData The data for the new task.
 * @returns The created task.
 */
export const createTask = async (taskData: Partial<Task>): Promise<Task> => {
  try {
    const response = await axios.post(API_URL, taskData, {
      ...AXIOS_CONFIG,
    });

    return response.data.content;
  } catch (error) {
    console.error("Error creating task:", error);
    throw error;
  }
};

/**
 * Update an existing task.
 *
 * @param taskId The ID of the task to update.
 * @param taskData The updated task data.
 * @returns The updated task.
 */
export const updateTask = async (taskData: Partial<Task>): Promise<Task> => {
  try {
    const response = await axios.put(`${API_URL}`, taskData, {
      ...AXIOS_CONFIG,
    });

    return response.data.content;
  } catch (error) {
    console.error("Error updating task:", error);
    throw error;
  }
};

/**
 * Delete a task.
 *
 * @param taskId The ID of the task to delete.
 * @returns The ID of the deleted task or null if deletion failed.
 */
export const deleteTask = async (
  taskId: number
): Promise<{ id: number } | null> => {
  try {
    const response = await axios.delete(`${API_URL}`, {
      ...AXIOS_CONFIG,
      data: {
        id: taskId,
      },
    });

    return response.data.content || null;
  } catch (error) {
    console.error("Error deleting task:", error);
    throw error;
  }
};
